import { useRef } from "react";
import { useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { Ico } from "../../components/icons/icons.jsx";
import { FloatingPanel, useDismissOnOutsideClick } from "../../components/ui/FloatingPanel.jsx";
import { STATUS_TINT, TIER_TINT } from "./accountConstants.js";

export function AccountCard({ account, onOpen, onEdit, onDelete, menuOpen, onMenuToggle }) {
  const { c, dark } = useThemeSlice();
  const menuBtnRef = useRef(null);
  const statusTint = STATUS_TINT[account.status] ?? "#94A3B8";
  const tierTint = TIER_TINT[account.tier] ?? c.mid;
  const cardLift = dark ? "0 0 0 1px rgba(255,255,255,0.05), 0 10px 36px rgba(0,0,0,0.4)" : "0 1px 2px rgba(0,0,0,0.04), 0 12px 36px rgba(0,0,0,0.08)";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(account)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(account);
        }
      }}
      style={{
        borderRadius: 14,
        border: `1px solid ${c.cardBdr}`,
        background: c.card,
        boxShadow: cardLift,
        padding: "16px 16px 14px 18px",
        cursor: "pointer",
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.12s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: statusTint }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${statusTint}1A`, color: statusTint }}>
            {Ico.building}
          </span>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{account.companyName}</h3>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 3, fontSize: 11, fontWeight: 700, color: statusTint, textTransform: "capitalize" }}>
              <span style={{ width: 7, height: 7, borderRadius: 99, background: statusTint }} />
              {account.status}
            </span>
          </div>
        </div>
        <button
          ref={menuBtnRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onMenuToggle(account.id);
          }}
          style={{ border: `1px solid ${c.sbBdr}`, background: c.hover, borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: c.mid, flexShrink: 0 }}
          aria-label="Actions"
        >
          {Ico.moreVertical}
        </button>
      </div>

      {menuOpen ? (
        <AccountActionMenu account={account} onEdit={onEdit} onDelete={onDelete} onClose={() => onMenuToggle(null)} anchorRef={menuBtnRef} />
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <TierBadge tier={account.tier} tint={tierTint} />
        <span style={{ fontSize: 12, color: c.mid }}>{account.city}, {account.state}</span>
      </div>

      <div style={{ paddingTop: 12, borderTop: `1px solid ${c.cardBdr}`, display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: c.sub }}>
        <span>{account.fleetSize} assets · ₹{account.mrr.toLocaleString("en-IN")}/mo</span>
        {account.contact?.email ? <span style={{ fontWeight: 600, color: c.mid }}>{account.contact.email}</span> : null}
      </div>
    </div>
  );
}

function TierBadge({ tier, tint }) {
  return (
    <span style={{ display: "inline-flex", padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: "capitalize", color: tint, background: `${tint}1A`, border: `1px solid ${tint}33` }}>
      {tier}
    </span>
  );
}

export function AccountActionMenu({ account, onEdit, onDelete, onClose, anchorRef }) {
  const { c, dark } = useThemeSlice();
  const panelRef = useRef(null);
  const cardLift = dark ? "0 10px 36px rgba(0,0,0,0.4)" : "0 12px 36px rgba(0,0,0,0.08)";

  useDismissOnOutsideClick(true, onClose, [anchorRef, panelRef]);

  return (
    <FloatingPanel anchorRef={anchorRef} panelRef={panelRef} open align="end" minWidth={160} gap={4}>
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ borderRadius: 10, border: `1px solid ${c.cardBdr}`, background: c.card, boxShadow: cardLift, padding: 6 }}
      >
        <MenuBtn label="Edit" icon={Ico.pencil} onClick={() => { onEdit(account); onClose(); }} c={c} />
        <MenuBtn label="Delete" icon={Ico.trash} onClick={() => { onDelete(account); onClose(); }} c={c} danger />
      </div>
    </FloatingPanel>
  );
}

function MenuBtn({ label, icon, onClick, c, danger }) {
  return (
    <button type="button" onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", border: "none", background: "none", padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: danger ? "#DC2626" : c.text }}>
      {icon} {label}
    </button>
  );
}
